import cloneDeep from 'lodash/cloneDeep'
import type {
  Cart,
  CartItem,
  GiftboxPrice,
  GiftcardAddon,
  GiftcardCartItem,
} from '~/composables/types/cart'
import { sendAddToCart } from '~/composables/useGtmEvents'

const CART_LOCAL_STORAGE_KEY_BASE = 'konfetti-cart-local-session' as string
const GIFTBOX_PRICE: GiftboxPrice = {
  amount: '295',
  currency: 'EUR',
  formatted: '€ 2.95',
}

export const cartLocalStorageKey = (key: string) =>
  `${CART_LOCAL_STORAGE_KEY_BASE}-${key}`

export const getEmptyCart = (): Cart =>
  cloneDeep<Cart>({
    items: [],
    giftboxPrice: GIFTBOX_PRICE,
    updatedAt: null,
    shippingCost: null,
  })

export type CartKeys = 'general-instance' | 'embedded-instance'

const useCart = (key: CartKeys | string) => {
  const {
    data: cart,
    loaded: cartLoaded,
    loadFromStorage,
  } = useKftLocalStorage<Cart>(cartLocalStorageKey(key), getEmptyCart())

  const loadingPricings = useState('useCart-loadingPricings', () => false)

  const { priceFactory } = usePricingsFactory()

  const lastRemoved = ref<CartItem | undefined>(undefined)

  const hasGiftcard = computed(() => cartGetters.hasGiftcard(cart.value))

  const findItem = (obj: CartItem): CartItem | undefined => {
    return cart.value.items.find((item: CartItem) => isTheSameItem(obj, item))
  }

  const debouncedFetchPricings = useDebounceFn(useFetchPricings, 300)

  const updateItemPricings = async (item: CartItem) => {
    if (
      item.type === 'GIFT_CARD' &&
      (item as GiftcardCartItem).giftcardType === 'VALUE'
    ) {
      item.pricings.price_breakdown[0].total_participants = item.quantity
      item.pricings.price.total_price = priceFactory(
        Number(item.pricings.price_breakdown[0].price_per_person.amount) *
          item.quantity
      )
      return
    }

    if (item.pricings) {
      ;(item as Partial<CartItem>).pricings = undefined
    }

    const dateId = ref(item.type === 'EVENT' ? item.id : undefined)

    loadingPricings.value = true

    try {
      const pricingsResponse = await debouncedFetchPricings(
        ref(item.eventDescriptionId),
        {
          dateId,
          quantity: ref(item.quantity),
          isPrivate: ref(item.private),
        }
      )

      item.pricings = pricingsResponse
    } catch (error) {
      console.error('Error fetching pricings:', error)
    } finally {
      loadingPricings.value = false
    }
  }

  const updateItemQuantity = async (
    obj: CartItem,
    newQuantity: number | string
  ): Promise<boolean> => {
    const item = findItem(obj)
    const availableTicketsQuantity = Number(item?.availableTicketsQuantity) || 0

    if (!item?.quantity) {
      return false
    }

    if (newQuantity === 0) {
      removeItem(obj)
    }

    if (
      !cartGetters.isGiftcard(item) &&
      availableTicketsQuantity < Number(newQuantity)
    ) {
      item.quantity = availableTicketsQuantity
      return false
    }

    item.quantity = Number(newQuantity)

    await updateItemPricings(item)

    return true
  }

  const isTheSameItem = (obj: CartItem, item: CartItem): boolean => {
    if (cartGetters.isGiftcard(obj)) {
      // For giftcards, compare multiple properties to ensure uniqueness
      return (
        cartGetters.isGiftcard(item) &&
        cartGetters.getGiftcardId(item) === cartGetters.getGiftcardId(obj) &&
        item.eventDescriptionId === obj.eventDescriptionId &&
        (item as any).giftcardType === (obj as any).giftcardType
      )
    }

    return cartGetters.getItemId(item) === cartGetters.getItemId(obj)
  }

  const isInCart = (obj: CartItem): boolean => {
    return cart.value.items.some((item: CartItem) => isTheSameItem(item, obj))
  }

  const addItem = async (item: CartItem): Promise<void> => {
    // Send GTM event no matter if it's already in cart
    sendAddToCart(item)

    // check if item is already in cart
    if (isInCart(item)) {
      // find the existing item
      const cartItem = findItem(item)
      // update existing item quantity
      await updateItemQuantity(item, (cartItem?.quantity || 0) + item.quantity)

      // update existing item addOn
      if (cartItem && item?.addOn) {
        cartItem.addOn = item.addOn
      }

      return
    }
    // push new item into cart
    cart.value.items.push(item)
  }

  const setLastRemovedItemTemporarily = (item: CartItem) => {
    lastRemoved.value = item

    setTimeout(() => {
      lastRemoved.value = undefined
    }, 15000)
  }

  const removeItem = (obj: CartItem) => {
    if (!process.client) {
      return null
    }

    let result = false

    const startingLength = cart.value.items.length
    if (obj) {
      setLastRemovedItemTemporarily(obj)
    }

    const toDeleteIdx = cart.value.items.findIndex((item) =>
      isTheSameItem(obj, item)
    )

    cart.value.items.splice(toDeleteIdx, 1)

    if (startingLength !== cart.value.items.length) {
      result = true
    }

    return result
  }

  const removeAddOn = (obj: CartItem, addOn: any) => {
    if (!process.client) {
      return null
    }

    const indexOfItemToRemoveAddOn = cart.value.items.findIndex((item) =>
      isTheSameItem(obj, item)
    )

    if (Array.isArray(cart.value.items?.[indexOfItemToRemoveAddOn]?.addOn)) {
      const addOns = cart.value.items?.[indexOfItemToRemoveAddOn]?.addOn
      const indexOfAddOnToRemove = addOns?.findIndex((a) => a.id === addOn.id)
      if (indexOfAddOnToRemove !== -1) {
        cart.value?.items?.[indexOfItemToRemoveAddOn]?.addOn.splice(
          indexOfAddOnToRemove,
          1
        )
      }
    } else {
      delete cart.value.items?.[indexOfItemToRemoveAddOn]?.addOn
    }
  }

  const toggleGiftcardAddon = (obj: GiftcardCartItem, addon: GiftcardAddon) => {
    const item = findItem(obj) as GiftcardCartItem

    item.giftcardAddon = addon

    return true
  }

  const updateItemProperty = <T extends CartItem>(
    obj: T,
    property: keyof T,
    value: any
  ): boolean => {
    // Find item by index to avoid issues with object comparison
    const itemIndex = cart.value.items.findIndex((item) => {
      if (cartGetters.isGiftcard(obj)) {
        return (
          cartGetters.isGiftcard(item) &&
          cartGetters.getGiftcardId(item) === cartGetters.getGiftcardId(obj) &&
          item.eventDescriptionId === obj.eventDescriptionId &&
          (item as any).giftcardType === (obj as any).giftcardType
        )
      }
      return cartGetters.getItemId(item) === cartGetters.getItemId(obj)
    })

    if (itemIndex === -1) {
      return false
    }

    // Update the item directly in the cart array
    ;(cart.value.items[itemIndex] as any)[property] = value
    return true
  }

  const clear = () => {
    cart.value = getEmptyCart()
  }

  return {
    cart,
    hasGiftcard,
    cartLoaded,
    loadingPricings: computed(() => loadingPricings.value),
    loadFromStorage,
    addItem,
    removeItem,
    removeAddOn,
    findItem,
    isInCart,
    updateItemQuantity,
    toggleGiftcardAddon,
    updateItemProperty,
    clear,
    updateItemPricings,
  }
}

export { useCart }
